// =========================================================================
// Catalog Manifest 型定義 + ローダー (Version Gate 付き)
// =========================================================================
// spec/closing_river_web_mvp_v2.md 「Catalog Manifest 仕様」 を実装。
// runtime / build-catalog 双方から import される (Node Built-in 非依存)。
//
// 絶対則:
//   1. Runtime MUST NOT call generateBoard.
//   2. Catalog entries are authoritative board snapshots; seed is provenance only.
//   3. Catalog-derived shortest/Silent metadata is valid only when
//      rulesFingerprint and solverFingerprint match.
//      cleanPossible は advisory (runtime Clean は ApplyResult/history から再計算)。
// =========================================================================

import type { SerializedState } from 'nagi-v9-engine';

// -------------------------------------------------------------------------
// 共通 ID / ラベル
// -------------------------------------------------------------------------

/** 安全な slug 文字列 (e.g. "main-easy-01" / "archive-bin3-07" / "tutorial-01") */
export type BoardId = string;

/** 難易度ラベル (PlayableBoard) */
export type DifficultyLabel = 'easy' | 'normal' | 'hard';

/** archive 用の難易度 bin (1..5) */
export type DifficultyBin = 1 | 2 | 3 | 4 | 5;

/** 画面に出すバッジ (Silent / Clean / Clear) */
export interface Badge {
  readonly cleared: boolean;
  readonly clean: boolean;
  readonly silent: boolean;
  /** Silent 達成時のみ (= shortest と等しい movesUsed) */
  readonly bestMoves?: number;
}

// -------------------------------------------------------------------------
// Board union (Playable / Tutorial)
// -------------------------------------------------------------------------

/** 1D 円環レイアウトのヒント。Phase 2 で 2D/2.5D に拡張する逃げ道 */
export interface LayoutHints {
  /** ノード ID → 固定座標 (任意)。指定がなければ renderer 側で円環配置 */
  readonly positions?: Record<string, { readonly x: number; readonly y: number }>;
}

/** main / archive 用の本番盤面 (catalog snapshot、絶対則 2) */
export interface PlayableBoard {
  readonly id: BoardId;
  readonly source: 'main' | 'archive';
  /** provenance only (engine の seed は string) */
  readonly seed: string;
  readonly state: SerializedState;
  /** Phase 2 で '2D' 等を追加可能 (型の逃げ道) */
  readonly dimension: '1D';
  readonly layoutKind: 'circle';
  readonly layoutHints?: LayoutHints;
  /** 手数上限 (= M + max(3, ceil(M*0.3))) */
  readonly N: number;
  /** BFS 最短手数 M */
  readonly shortest: number;
  /** 上限カウント済 */
  readonly shortestSolutionCount: number;
  /** advisory: runtime Clean は ApplyResult/history から再計算 (絶対則 3) */
  readonly cleanPossible: boolean;
  /** 100 trial 中の詰み率 */
  readonly greedyStuckRate: number;
  /** 最初の数手の平均分岐数 */
  readonly branchingFactor: number;
  /** 最大同値バケットサイズ k (1..5) */
  readonly maxSameValue: number;
  readonly uniqueValueCount: number;
  readonly graphMetrics: {
    readonly chordCount?: number;
    readonly edgeDensity?: number;
  };
  readonly difficultyLabel: DifficultyLabel;
  /** archive のみ */
  readonly difficultyBin?: DifficultyBin;
  /**
   * codex R7 「Web Catalog Exception」: build-time prevalidation で
   * solver wall-clock p99 が spec §8.1 hard limit (= 5000ms) を超過した盤面。
   * 現状 Hard bin (10N/12E, M=11) はこの条件に該当 (p99 ~6100ms)。
   * runtime には影響しない (solver は runtime で走らない、catalog snapshot のみ使用)
   * が provenance として隠さず明示する。W7 list UI で警告アイコン等を出してよい。
   */
  readonly overTelemetryLimit?: boolean;
}

/** チュートリアル 1 step の scripted シーケンス (designer 手書き) */
export interface TutorialStepScript {
  readonly stepIndex: number;
  /** step の説明文 (短く、ユーザー向け) */
  readonly prompt: string;
  /** step 2/3 で実タップする必要のある edge (engine の EdgeId は number) */
  readonly requiredEdgeId?: number;
  /** 失敗時のヒント (controller が表示) */
  readonly validationHint?: string;
}

/** handmade tutorial 盤面 (§8.3 対象外、Silent/Clean/Clear catalog 記録対象外) */
export interface TutorialBoard {
  readonly id: BoardId;
  readonly source: 'handmade-tutorial';
  readonly state: SerializedState;
  readonly dimension: '1D';
  readonly layoutKind: 'circle';
  readonly layoutHints?: LayoutHints;
  readonly script: readonly TutorialStepScript[];
  /** チュートリアル完了後の state hash (designer が手で検算) */
  readonly expectedStateHash: string;
}

export type CatalogBoard = PlayableBoard | TutorialBoard;

// -------------------------------------------------------------------------
// 集計 / Manifest 本体
// -------------------------------------------------------------------------

export interface CatalogAggregate {
  readonly attempted: number;
  readonly accepted: number;
  readonly kHistogram: Readonly<Record<'k=1' | 'k=2' | 'k=3' | 'k=4' | 'k=5', number>>;
  readonly solverMsP50: number;
  readonly solverMsP95: number;
  readonly solverMsP99: number;
  readonly cleanPossibleRatio: number;
}

/** spec 「Catalog Manifest 仕様」 そのまま */
export interface CatalogManifest {
  readonly schemaVersion: 1;
  readonly specVersion: 'closing_river_v10.5';
  readonly enginePackageName: 'nagi-v9-engine';
  /** build-time に ENGINE_VERSION から流す (browser から package.json を読まない) */
  readonly engineVersion: string;
  /** hash(types + boardState + operation + judge + cleanEventDetector) */
  readonly rulesFingerprint: string;
  /** hash(solver) */
  readonly solverFingerprint: string;
  /** hash(generator + seedRetry + prng) — provenance のみ */
  readonly generatorFingerprint: string;
  /** ISO8601 */
  readonly generatedAt: string;
  /** playable manifest のみ、tutorial では未使用 */
  readonly sourceSeedRange?: { readonly from: number; readonly to: number };
  /** playable manifest のみ */
  readonly aggregate?: CatalogAggregate;
  readonly boards: readonly CatalogBoard[];
}

// -------------------------------------------------------------------------
// ViewModel (W3 renderer / W4 input / W5 controller 共用)
// -------------------------------------------------------------------------

/**
 * SVG renderer に渡す「描く分の board snapshot」。
 * controller が GameState + layout から build する。catalog manifest とは別物。
 * (型を catalog.ts に置くのは W3-W7 が import する hub にするため)
 */
export interface BoardViewModel {
  readonly boardId: BoardId;
  readonly dimension: '1D';
  readonly nodes: ReadonlyArray<{
    readonly id: number;
    readonly value: number;
    readonly x: number;
    readonly y: number;
    /** タップで次に出るプレビュー値 (任意) */
    readonly previewValue?: number;
  }>;
  readonly activeEdges: ReadonlyArray<{
    readonly id: number;
    readonly src: number;
    readonly dst: number;
    /** 描画ハイライト状態 (W3 が線質差カタログを切り替える) */
    readonly highlight: 'none' | 'tappable' | 'selected';
  }>;
  readonly historyEdges: ReadonlyArray<{
    readonly id: number;
    readonly fromPos: readonly [number, number];
    readonly toPos: readonly [number, number];
    /** 何手前か (renderer が opacity decay に使う) */
    readonly age: number;
    readonly reason: 'consumed' | 'self_loop';
  }>;
  readonly movesUsed: number;
  readonly turnLimit: number;
  readonly seedLabel: string;
}

// -------------------------------------------------------------------------
// Version Gate (catalog ↔ engine fingerprint 整合)
// -------------------------------------------------------------------------

export type VersionGateMode = 'dev' | 'prod';

export interface VersionGateEnvFingerprints {
  readonly rulesFingerprint: string;
  readonly solverFingerprint: string;
  readonly generatorFingerprint: string;
  readonly enginePackageName: string;
  readonly engineVersion: string;
}

export type VersionGateOutcome =
  | { readonly kind: 'ok' }
  | {
      readonly kind: 'rules_mismatch';
      readonly catalog: string;
      readonly env: string;
      readonly mode: VersionGateMode;
    }
  | {
      readonly kind: 'solver_mismatch';
      readonly catalog: string;
      readonly env: string;
    }
  | {
      readonly kind: 'generator_mismatch';
      readonly catalog: string;
      readonly env: string;
    }
  | {
      readonly kind: 'schema_mismatch';
      readonly expected: 1;
      readonly got: number;
    }
  | {
      readonly kind: 'engine_name_mismatch';
      readonly expected: 'nagi-v9-engine';
      readonly got: string;
    };

/**
 * spec 「Version Gate」:
 *   schemaVersion 不一致      → 読まない (build 段階で catch、runtime は schema_mismatch)
 *   rulesFingerprint 不一致   → dev: hard fail / prod: fatal screen (操作不可)
 *   solverFingerprint 不一致  → catalog-derived shortest/Silent は無効、Clear のみ評価
 *   generatorFingerprint 不一致 → 警告のみ (gameplay 影響なし)
 *   engineVersion patch 差分  → rulesFingerprint 同じなら許容
 *
 * 戻り値は「最初に当たった非 ok」 + ok のいずれか。複数の mismatch がある場合は
 * 重大度順 (schema → engine_name → rules → solver → generator)。
 */
export function evaluateVersionGate(
  manifest: CatalogManifest,
  env: VersionGateEnvFingerprints,
): VersionGateOutcome {
  if (manifest.schemaVersion !== 1) {
    return {
      kind: 'schema_mismatch',
      expected: 1,
      got: manifest.schemaVersion as number,
    };
  }
  if (manifest.enginePackageName !== env.enginePackageName) {
    return {
      kind: 'engine_name_mismatch',
      expected: 'nagi-v9-engine',
      got: env.enginePackageName,
    };
  }
  if (manifest.rulesFingerprint !== env.rulesFingerprint) {
    return {
      kind: 'rules_mismatch',
      catalog: manifest.rulesFingerprint,
      env: env.rulesFingerprint,
      mode: 'dev', // 呼び出し側で mode を別途参照する場合は再評価。ここでは情報のみ
    };
  }
  if (manifest.solverFingerprint !== env.solverFingerprint) {
    return {
      kind: 'solver_mismatch',
      catalog: manifest.solverFingerprint,
      env: env.solverFingerprint,
    };
  }
  if (manifest.generatorFingerprint !== env.generatorFingerprint) {
    return {
      kind: 'generator_mismatch',
      catalog: manifest.generatorFingerprint,
      env: env.generatorFingerprint,
    };
  }
  return { kind: 'ok' };
}

// -------------------------------------------------------------------------
// Loader
// -------------------------------------------------------------------------

/**
 * raw JSON (= import した main.json / archive.json / tutorial.json) を
 * CatalogManifest として「型として」 受け取り、最低限の構造検査を行う。
 *
 * fingerprint gate は呼び出し側で evaluateVersionGate を別途呼ぶ
 * (env fingerprint の解決方法はホスト側依存のため、ここでは混ぜない)。
 *
 * 失敗時は throw する (runtime で catch して fatal screen)。
 */
export function loadCatalogManifest(raw: unknown): CatalogManifest {
  if (raw === null || typeof raw !== 'object') {
    throw new Error('loadCatalogManifest: raw is not an object');
  }
  const m = raw as Partial<CatalogManifest>;
  if (m.schemaVersion !== 1) {
    throw new Error(
      `loadCatalogManifest: schemaVersion mismatch (expected 1, got ${String(m.schemaVersion)})`,
    );
  }
  if (m.specVersion !== 'closing_river_v10.5') {
    throw new Error(
      `loadCatalogManifest: specVersion mismatch (expected 'closing_river_v10.5', got '${String(
        m.specVersion,
      )}')`,
    );
  }
  if (m.enginePackageName !== 'nagi-v9-engine') {
    throw new Error(
      `loadCatalogManifest: enginePackageName mismatch (got '${String(m.enginePackageName)}')`,
    );
  }
  if (
    typeof m.rulesFingerprint !== 'string' ||
    typeof m.solverFingerprint !== 'string' ||
    typeof m.generatorFingerprint !== 'string'
  ) {
    throw new Error('loadCatalogManifest: fingerprint fields missing or not string');
  }
  if (typeof m.engineVersion !== 'string') {
    throw new Error('loadCatalogManifest: engineVersion missing');
  }
  if (typeof m.generatedAt !== 'string') {
    throw new Error('loadCatalogManifest: generatedAt missing');
  }
  if (!Array.isArray(m.boards)) {
    throw new Error('loadCatalogManifest: boards is not an array');
  }
  // 中身は loose 検査 (各 board の deep validation は engine adapter loadBoard 側で実施)
  return m as CatalogManifest;
}
